// ============================================
// InventoryLossStatsPage
// Thống kê hao hụt theo nguyên nhân (sheet 2 — mục #220)
// ============================================
//
// Nguồn: GET /inventory/loss-stats/. BE code xong 09/09/2026, lên QA đợt
// deploy kế — CÙNG đợt với field `loss_reason` trên adjust/bulk-adjust, nên
// hai màn (nhập liệu + thống kê) đi vào sản xuất cùng lúc.
//
// `by_reason` LUÔN có đủ 4 phần tử do BE zero-fill — không cần tự bù ở đây,
// và KHÔNG được tự sắp xếp lại: thứ tự BE trả về là thứ tự cố định để biểu đồ
// không nhảy giữa các kỳ lọc.

import { useState } from 'react';
import { useI18n, resolveDateLocale } from '../../i18n';
import { PageContainer, PageHeader, SelectDropdown } from '../../components/shared';
import { useLossStats } from '../../hooks';
import { useStoreSelectionStore } from '../../stores';

const REASON_COLORS: Record<string, string> = {
  THEFT: 'bg-red-500',
  EXPIRED: 'bg-amber-500',
  DAMAGED: 'bg-orange-500',
  OTHER: 'bg-slate-400',
};

const PERIOD_OPTIONS = [
  { value: '7', labelKey: 'driftDaysOption' },
  { value: '30', labelKey: 'driftDaysOption' },
  { value: '90', labelKey: 'driftDaysOption' },
];

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Số lượng hao hụt, định dạng theo locale (bug #242).
 *
 * BE đã đổi `quantity` từ chuỗi decimal (`"3.000"`) sang SỐ (`3`) ngày
 * 18/09/2026. Hàm này KHÔNG bỏ đi được, vì hai lý do:
 *
 * 1. Bản BE mới còn ở nhánh local, chưa lên QA — QA hiện vẫn trả chuỗi, và in
 *    thẳng `"3.000"` ra là đọc thành ba nghìn, sai ba bậc độ lớn ngay giữa màn
 *    thống kê hao hụt.
 * 2. Kể cả khi đã là số, vẫn cần `Intl` cho dấu phân cách hàng nghìn — `12345`
 *    phải đọc ra `12,345`, và dấu phân cách khác nhau giữa en/pl/uk.
 *
 * Nhận cả hai kiểu nên không có mốc phải sửa lại lúc BE lên QA.
 */
function formatQuantity(value: string | number | null | undefined, locale: string): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(num);
}

export function InventoryLossStatsPage() {
  const { t, language } = useI18n();
  const numberLocale = resolveDateLocale(language);
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);
  const [periodDays, setPeriodDays] = useState('30');

  const endDate = toDateInput(new Date());
  const startDate = toDateInput(
    new Date(Date.now() - Number(periodDays) * 24 * 60 * 60 * 1000)
  );

  const { data, isLoading, error } = useLossStats({
    store_id: selectedStoreId || undefined,
    start_date: startDate,
    end_date: endDate,
  });

  // 404 nghĩa là "chưa lên QA", KHÔNG phải "không có hao hụt" — hai chuyện
  // khác hẳn nhau, và nói nhầm thì người quản lý tưởng cửa hàng sạch sẽ.
  // Đọc `status` thật từ `ApiClientError` (`packages/api-client/src/types.ts`),
  // không đoán qua regex trên message — message có thể đổi câu chữ bất cứ lúc
  // nào mà status thì không.
  const notDeployed = (error as { status?: number } | null)?.status === 404;

  const byReason = data?.by_reason ?? [];
  const maxCount = Math.max(1, ...byReason.map((r) => r.adjustment_count));

  return (
    <PageContainer>
      <PageHeader title={t('lossStatsTitle')} subtitle={t('lossStatsSubtitle')} />

      {notDeployed ? (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-sm font-semibold text-amber-800">{t('lossStatsNotDeployed')}</p>
          <p className="mx-auto mt-2 max-w-xl text-xs text-amber-700">
            {t('lossStatsNotDeployedHint')}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3">
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('driftDays')}
            </label>
            <SelectDropdown
              options={PERIOD_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey).replace('{days}', o.value),
              }))}
              value={periodDays}
              onChange={setPeriodDays}
              className="w-full sm:w-[200px]"
            />
          </div>

          {error && (
            <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
              {/* Lỗi ở đây là `ApiClientError` — một OBJECT thường, không phải
                  `Error` — nên `instanceof Error` luôn sai và bỏ sót message
                  thật. Đọc `.message` trực tiếp, `Error` chỉ là dự phòng. */}
              {(error as { message?: string })?.message ||
                (error instanceof Error ? error.message : t('lossStatsLoadError'))}
            </div>
          )}

          {!error && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              {isLoading ? (
                <p className="py-8 text-center text-sm text-slate-500">{t('loading')}</p>
              ) : byReason.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">{t('lossStatsEmpty')}</p>
              ) : (
                <>
                  <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {byReason.map((r) => (
                      <div key={r.loss_reason} className="rounded-lg bg-slate-50 p-3">
                        <div className="flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${REASON_COLORS[r.loss_reason] ?? 'bg-slate-400'}`} />
                          <span className="text-xs font-semibold text-slate-500">{r.label}</span>
                        </div>
                        <p className="mt-1 text-lg font-bold text-slate-900">
                          {formatQuantity(r.quantity, numberLocale)}
                        </p>
                        <p className="text-xs text-slate-400">
                          {t('lossStatsAdjustmentCount').replace('{count}', String(r.adjustment_count))}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Cột ngang đơn giản theo `adjustment_count` — đủ để nhìn ra
                      nguyên nhân nào chiếm ưu thế mà không cần thư viện chart. */}
                  <div className="space-y-2">
                    {byReason.map((r) => (
                      <div key={r.loss_reason} className="flex items-center gap-3">
                        <span className="w-20 shrink-0 text-xs font-semibold text-slate-600">{r.label}</span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full ${REASON_COLORS[r.loss_reason] ?? 'bg-slate-400'}`}
                            style={{ width: `${(r.adjustment_count / maxCount) * 100}%` }}
                          />
                        </div>
                        <span className="w-10 shrink-0 text-right text-xs font-semibold text-slate-500">
                          {r.adjustment_count}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 border-t border-slate-100 pt-4 text-sm text-slate-600">
                    {t('lossStatsTotal')
                      .replace('{count}', String(data?.total.adjustment_count ?? 0))
                      .replace('{quantity}', formatQuantity(data?.total.quantity, numberLocale))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
